/**
 * In-sidebar session picker for the meeting host.
 *
 * Shown when the app opens in a meeting that has no linked session and the
 * server has verified the caller as the host with a connected Facilitator
 * Tools account (exchange-token → status 'unlinked', canLink). Lists the
 * host's sessions; choosing one links it to this meeting and hands back a
 * facilitator session token, so the app carries straight on.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listHostSessions,
  linkHostSession,
  type HostSessionSummary,
  type LinkedSession,
} from '../lib/api';
import { openExternal, webBaseUrl } from '../lib/zoomActions';

interface SessionPickerProps {
  hostToken: string;
  meetingTopic?: string;
  onLinked: (result: LinkedSession) => void;
  /** The host token (30 min) expired: run the Zoom authorize flow again. */
  onExpired: () => void;
}

function isExpiredError(err: unknown): boolean {
  return err instanceof Error && /API 401/.test(err.message);
}

function formatUpdated(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return 'Edited today';
  if (days === 1) return 'Edited yesterday';
  if (days < 30) return `Edited ${days} days ago`;
  return `Edited ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

export function SessionPicker({ hostToken, meetingTopic, onLinked, onExpired }: SessionPickerProps) {
  const [sessions, setSessions] = useState<HostSessionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      setSessions(await listHostSessions(hostToken));
    } catch (err) {
      if (isExpiredError(err)) {
        onExpired();
        return;
      }
      setError('Could not load your sessions. Check your connection and try again.');
      setSessions((prev) => prev ?? []);
    }
  }, [hostToken, onExpired]);

  useEffect(() => {
    load();
  }, [load]);

  const choose = async (id: string) => {
    if (linkingId) return;
    setLinkingId(id);
    setError(null);
    try {
      const result = await linkHostSession(hostToken, id);
      onLinked(result);
    } catch (err) {
      if (isExpiredError(err)) {
        onExpired();
        return;
      }
      setError('Could not link that session. Please try again.');
      setLinkingId(null);
    }
  };

  const visible = useMemo(() => {
    if (!sessions) return [];
    const q = query.trim().toLowerCase();
    return q ? sessions.filter((s) => s.title.toLowerCase().includes(q)) : sessions;
  }, [sessions, query]);

  const openWebApp = () => {
    openExternal(`${webBaseUrl()}/`).catch(() => {});
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <div className="px-4 pt-5 pb-3 border-b border-gray-100">
        <h1 className="text-base font-semibold text-gray-900">Choose a session to run</h1>
        <p className="text-xs text-gray-500 mt-1">
          {meetingTopic ? `It will be linked to “${meetingTopic}”.` : 'It will be linked to this meeting.'}{' '}
          Participants open it from Apps or your invitation.
        </p>
        {sessions && sessions.length > 6 && (
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your sessions"
            aria-label="Search your sessions"
            className="mt-3 w-full text-sm border border-gray-200 rounded-lg px-3 py-2
              focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-500"
          />
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3">
        {sessions === null && (
          <div className="flex items-center gap-3 text-sm text-gray-500 py-6 justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-500 border-t-transparent" />
            Loading your sessions…
          </div>
        )}

        {sessions !== null && sessions.length === 0 && (
          <div className="text-center py-8">
            <p className="text-sm font-medium text-gray-900">No sessions yet</p>
            <p className="text-xs text-gray-500 mt-1">
              Create one in Facilitator Tools, add a poll or question, then come back here and choose it.
            </p>
            <button
              onClick={openWebApp}
              className="mt-4 px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors"
            >
              Open Facilitator Tools
            </button>
          </div>
        )}

        {sessions !== null && sessions.length > 0 && visible.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-6">No sessions match “{query}”.</p>
        )}

        <ul className="space-y-2">
          {visible.map((s) => {
            const busy = linkingId === s.id;
            return (
              <li key={s.id}>
                <button
                  onClick={() => choose(s.id)}
                  disabled={!!linkingId}
                  className="w-full text-left border border-gray-200 rounded-xl px-3 py-2.5
                    hover:border-primary-300 hover:bg-primary-50 transition-colors
                    disabled:opacity-60 disabled:cursor-wait
                    focus:outline-none focus:ring-2 focus:ring-primary-200"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-gray-900 truncate">{s.title}</span>
                    {busy ? (
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent flex-shrink-0" />
                    ) : (
                      <span className="text-xs font-medium text-primary-600 flex-shrink-0">Use</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {s.activityCount === 1 ? '1 activity' : `${s.activityCount} activities`}
                    {formatUpdated(s.updatedAt) ? ` · ${formatUpdated(s.updatedAt)}` : ''}
                  </p>
                </button>
              </li>
            );
          })}
        </ul>

        {error && (
          <p role="alert" className="text-xs text-red-600 mt-3 text-center">
            {error}
          </p>
        )}
      </div>

      <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-xs">
        <button onClick={openWebApp} className="text-primary-600 font-medium hover:underline">
          Create a new session
        </button>
        <button onClick={load} className="text-gray-500 hover:text-gray-700">
          Refresh list
        </button>
      </div>
    </div>
  );
}
